import { setScreen, card, title, el, colors, btn, divider } from "./app";
import { GameSession } from "../client/ws/GameSession";
import type { GameClient } from "../client/controller/GameClient";
import type { GameStateSnapshotMessage, ActionPromptMessage } from "../client/ws/OutboundMessage";
import type { InboundMessage } from "../client/ws/InboundMessage";
import type { LocalGameState } from "../client/model/LocalGameState";
import { showLobby } from "./lobby";
import { startGame } from "../game";

/**
 * Share code = gameId + ":" + joinCode.
 * The joining player pastes this whole string.
 */
function encodeShareCode(gameId: string, joinCode: string): string {
  return `${gameId}:${joinCode}`;
}

export function showWaitingRoom(gameId: string, joinCode: string, token: string): void {
  const shareCode = encodeShareCode(gameId, joinCode);

  const c = card(500);

  c.appendChild(title("Game Created!"));

  const intro = el("p", { margin: "0 0 24px 0", color: colors.muted, fontSize: "13px" });
  intro.textContent = "Share this code with your opponent so they can join.";
  c.appendChild(intro);

  // Share code display
  const codeLabel = el("p", {
    fontSize: "11px",
    color: colors.muted,
    letterSpacing: "1px",
    textTransform: "uppercase",
    margin: "0 0 8px 0",
  });
  codeLabel.textContent = "Game Code";
  c.appendChild(codeLabel);

  const codeBox = el("div", {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#0a0a14",
    border: `1px solid ${colors.border}`,
    borderRadius: "6px",
    padding: "12px 16px",
    marginBottom: "24px",
  });

  const codeText = el("span", {
    flex: "1",
    fontSize: "17px",
    fontWeight: "bold",
    color: colors.gold,
    letterSpacing: "2px",
    wordBreak: "break-all",
  });
  codeText.textContent = shareCode;

  const copyBtn = btn("Copy", "secondary");
  copyBtn.style.flexShrink = "0";
  copyBtn.style.padding = "7px 14px";
  copyBtn.style.fontSize = "12px";
  copyBtn.addEventListener("click", async () => {
    await navigator.clipboard.writeText(shareCode);
    copyBtn.textContent = "Copied!";
    copyBtn.style.color = colors.success;
    setTimeout(() => {
      copyBtn.textContent = "Copy";
      copyBtn.style.color = "";
    }, 2000);
  });

  codeBox.appendChild(codeText);
  codeBox.appendChild(copyBtn);
  c.appendChild(codeBox);

  c.appendChild(divider());

  // Status section
  const statusRow = el("div", {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "24px",
  });

  const spinner = el("div", {
    width: "16px",
    height: "16px",
    border: `2px solid ${colors.border}`,
    borderTop: `2px solid ${colors.accent}`,
    borderRadius: "50%",
    flexShrink: "0",
  });
  // CSS animation via stylesheet injection (avoid external CSS)
  if (!document.getElementById("bb-spinner-style")) {
    const style = document.createElement("style");
    style.id = "bb-spinner-style";
    style.textContent = `@keyframes bb-spin { to { transform: rotate(360deg); } }`;
    document.head.appendChild(style);
  }
  spinner.style.animation = "bb-spin 0.8s linear infinite";

  const statusText = el("span", { fontSize: "14px", color: colors.muted });
  statusText.textContent = "Waiting for opponent…";

  statusRow.appendChild(spinner);
  statusRow.appendChild(statusText);
  c.appendChild(statusRow);

  const leaveBtn = btn("← Leave", "ghost");
  leaveBtn.style.fontSize = "13px";
  leaveBtn.style.color = colors.muted;
  c.appendChild(leaveBtn);

  setScreen(c);

  // Connect WebSocket
  let session: GameSession | null = null;

  const client: GameClient = {
    onSnapshot(snap: GameStateSnapshotMessage, _state: LocalGameState) {
      statusText.textContent = snap.phase === "WAITING_FOR_OPPONENT" ? "Waiting for opponent…" : snap.phase;
      spinner.style.borderTopColor = colors.success;
    },
    onEventBatch(_events, _state) {},
    onFormationAccepted() {
      statusText.textContent = "Formation accepted. Game starting…";
    },
    onError(error: string) {
      statusText.textContent = `Error: ${error}`;
      spinner.style.display = "none";
    },
    async onActionPrompt(_prompt: ActionPromptMessage, _state: LocalGameState): Promise<InboundMessage | null> {
      return null;
    },
    onGameStarted(_state) {
      startGame(session!, true);
    },
  };

  session = new GameSession(gameId, token, client);
  session.connect();

  // Wire leave button
  leaveBtn.addEventListener("click", () => {
    session?.disconnect();
    showLobby();
  });
}

export function decodeShareCode(shareCode: string): { gameId: string; joinCode: string } | null {
  const idx = shareCode.indexOf(":");
  if (idx <= 0) return null;
  return {
    gameId: shareCode.slice(0, idx),
    joinCode: shareCode.slice(idx + 1),
  };
}
